import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  showTicketDetailsPopup,
  hideTicketDetailsPopup,
  setCompanyTickets,
} from "../../Pages/Store";
import TicketDetails from "./TicketDetails";
import { useNavigate, Link, useParams } from "react-router-dom";
import { statusColors } from "../../Pages/Config";
import { getCompanyTickets, getErrorMessage } from "../../services/api";
import "../../Styles/SelfService.css";
import { toast } from "react-toastify";
import useIdleTimer from "../../Pages/UseIdleTimer";
import Avatar from "@mui/material/Avatar";
import {
  Divider,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import UserPng from "../../Assets/user-icon.png";
import logoDark from "../../Assets/logo-dark.svg";

const CompanyTickets = () => {
  const navigate = useNavigate();
  const { customerId } = useParams(); // Get customer_id from URL params
  const [ticketNumber, setTicketNumber] = useState("");
  const [sortValue, setSortValue] = useState("All");
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const dispatch = useDispatch();
  const companyTickets = useSelector((state) => state.root.companyTickets);
  const {
    showTicketDetailsPopup: showTicketDetails,
    ticketDetails,
  } = useSelector((state) => state.root);
  const login = localStorage.getItem("Login");

  const handleLogout = () => {
    navigate("/");
    localStorage.removeItem("Login");
    localStorage.removeItem("ssCreds");
    localStorage.removeItem("sessionId");
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useIdleTimer(handleLogout, 900000);

  useEffect(() => {
    if (!login) {
      handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  useEffect(() => {
    dispatch(hideTicketDetailsPopup());
    handleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]); // Re-fetch when customerId changes

  const handleData = async () => {
    setLoading(true);
    try {
      const { username, password } = JSON.parse(
        localStorage.getItem("ssCreds") || "{}"
      );

      // Use customer_id from URL params (passed from SelfService page)
      if (!customerId) {
        toast.warn("Customer ID not found. Please navigate from Self-Service page.", {
          position: "top-right",
          autoClose: 3000,
        });
        dispatch(setCompanyTickets([]));
        navigate("/Self-Service"); // Redirect to Self-Service if no customer_id
        return;
      }

      const response = await getCompanyTickets(username, password, customerId);
      dispatch(setCompanyTickets(response.data.tickets));
    } catch (error) {
      dispatch(setCompanyTickets([]));
      toast.error(getErrorMessage(error, "Failed to load company tickets"), {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSortChange({ target: { value: sortValue } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyTickets]);

  useEffect(() => {
    paginateTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTickets, currentPage, pageSize]);

  const handleSortChange = (event) => {
    const selectedValue = event.target.value;
    setSortValue(selectedValue);

    if (selectedValue === "All") {
      setFilteredTickets(companyTickets);
    } else {
      setFilteredTickets(
        companyTickets.filter((ticket) => ticket.status === selectedValue)
      );
    }
    setCurrentPage(1);
  };

  const searchTicket = () => {
    if (ticketNumber.trim()) {
      const found = companyTickets.find(
        (ticket) => String(ticket.caseNumber) === ticketNumber.trim()
      );

      if (found) {
        dispatch(showTicketDetailsPopup(found, "company"));
      } else {
        toast.warn("Ticket not found", {
          position: "top-right",
          autoClose: 2000,
        });
      }
      setTicketNumber("");
    } else {
      toast.warn("Enter case number to search", {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const totalTickets = companyTickets?.length || 0;

  const uniqueStatuses = React.useMemo(() => {
    if (!companyTickets || companyTickets.length === 0) return [];
    const statuses = new Set(
      companyTickets.map((ticket) => ticket.status).filter(Boolean)
    );
    return Array.from(statuses).sort();
  }, [companyTickets]);

  const paginateTickets = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const sortedTickets = filteredTickets.sort((a, b) => {
      const dateA = new Date(a.createdOn || 0);
      const dateB = new Date(b.createdOn || 0);
      return dateB - dateA;
    });
    return sortedTickets.slice(startIndex, endIndex);
  };

  const handleSearch = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      searchTicket();
    }
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredTickets.length / pageSize);

  const Pagination = ({ totalPages, currentPage, handlePageChange }) => {
    const pageNumbers = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1, 2);

      if (currentPage > 6 && currentPage < totalPages - 2) {
        pageNumbers.push("...");
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        pageNumbers.push("...");
      } else if (currentPage <= 3) {
        pageNumbers.push(3, 4);
        pageNumbers.push("...");
      } else {
        pageNumbers.push("...");
        pageNumbers.push(totalPages - 3, totalPages - 2);
      }
      pageNumbers.push(totalPages - 1, totalPages);
    }

    return (
      <nav>
        <ul className="pagination justify-content-center mt-3 mt-md-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &laquo;
            </button>
          </li>
          {pageNumbers.map((number, index) => (
            <li
              key={index}
              className={`page-item ${number === currentPage ? "active" : ""}`}
            >
              {number === "..." ? (
                <span className="page-link">{number}</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => handlePageChange(number)}
                >
                  {number}
                </button>
              )}
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              &raquo;
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="container-fluid ss-page">
      <div className="row breadcrumb-contain ss-topbar">
        <div className="col-xl-6 col-lg-6 col-md-8 col-sm-8 col-8 breadcrumb-nav">
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              className="breadcrumb-home"
              underline="hover"
              color="inherit"
              onClick={() => navigate("/Self-Service")}
              style={{ cursor: "pointer" }}
            >
              <img src={logoDark} alt="Self Service" className="breadcrumb-logo" />
            </Link>
            <span className="breadcrumb-active">Company Tickets</span>
          </Breadcrumbs>
        </div>
        <div className="col-xl-6 col-lg-6 col-md-4 col-sm-4 col-4 nav-profile ">
          <div
            className={`profile-trigger ${Boolean(anchorEl) ? "profile-trigger-open" : ""}`}
            onClick={handleMenu}
          >
            <Avatar src={UserPng} className="profile-avatar" />
            <span className="profile-trigger-name">
              {(localStorage.getItem("name") || "Account").split(" ")[0]}
            </span>
            <KeyboardArrowDownIcon className="profile-trigger-caret" fontSize="small" />
          </div>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: {
                  mt: 1.25,
                  minWidth: 240,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
                  overflow: "hidden",
                },
              },
            }}
          >
            <div className="profile-menu-compact">
              <div className="profile-menu-name">
                <PersonIcon fontSize="small" className="profile-menu-row-icon" />
                {localStorage.getItem("name")}
              </div>
              <div className="profile-menu-email">
                <EmailIcon fontSize="small" className="profile-menu-row-icon" />
                {localStorage.getItem("email")}
              </div>
            </div>
            <Divider />
            <MenuItem onClick={handleLogout} className="profile-menu-logout-compact">
              <LogoutIcon fontSize="small" className="profile-menu-logout-icon" />
              Logout
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="ss-toolbar-card">
        <div className="row ticket-table-nav">
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 ticket-count d-flex align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-ss-primary"
              onClick={() => navigate("/Self-Service")}
              title="Go back"
            >
              <ArrowBackIcon fontSize="small" />
              Back
            </button>
            <div className="total-ticket stat-chip">
              <b>Total Company Tickets: {totalTickets}</b>
            </div>
          </div>
          <div className="col-xl-8 col-lg-8 col-md-8 col-sm-12 ticket-buttons">
            <div className="search-input">
              <input
                type="text"
                className="form-control mb-2 mb-md-0 me-md-0"
                id="companyTicketNumber"
                placeholder="Search Case Number"
                value={ticketNumber}
                onKeyDown={handleSearch}
                onChange={(e) => setTicketNumber(e.target.value)}
              />
              <button type="button" onClick={searchTicket}>
                <i className="fa fa-search" aria-hidden="true"></i>
              </button>
            </div>
            <div
              className="form-group mb-2 mb-md-0 filter-btn"
              style={{ width: "auto" }}
            >
              <div className="select-with-icon">
                <FilterListIcon fontSize="small" className="select-icon" />
                <Select
                  className="ss-mui-select"
                  id="companySortSelect"
                  onChange={handleSortChange}
                  title="Filter by Status"
                  value={sortValue}
                  variant="outlined"
                  size="small"
                  MenuProps={{ slotProps: { paper: { className: "ss-select-menu-paper" } } }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {uniqueStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      <span
                        className="status-dot-inline"
                        style={{ background: statusColors[status] || statusColors.Default }}
                      ></span>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ss-table-card">
      <div className="table-responsive">
        {!loading && (totalTickets === 0 || filteredTickets?.length === 0) ? (
          <div className="text-center">No company tickets available</div>
        ) : (
          <>
            <table className="table table-bordered text-start">
              <thead style={{ textWrap: "nowrap" }}>
                <tr>
                  <th scope="col">SL NO</th>
                  <th scope="col">Case Number</th>
                  <th scope="col">Case Title</th>
                  <th scope="col">Ticket Type</th>
                  <th scope="col">Raised On</th>
                  <th scope="col">Assigned To</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginateTickets().map((ticket, index) => {
                  const statusColor =
                    statusColors[ticket.status] || statusColors.Default;

                  return (
                    <tr key={index}>
                      <td style={{ borderLeft: `4px solid ${statusColor}` }}>
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td>
                        <a
                          id="user-button"
                          onClick={() =>
                            dispatch(showTicketDetailsPopup(ticket, "company"))
                          }
                        >
                          {ticket.caseNumber || "-"}
                        </a>
                      </td>
                      <td>{ticket.title || "-"}</td>
                      <td>{ticket.ticketType || "-"}</td>
                      <td style={{ textWrap: "nowrap" }}>
                        {ticket.createdOn?.split(/[ T]/)[0] || "-"}
                      </td>
                      <td>{ticket.assignedTo || "-"}</td>
                      <td>
                        <span className="status-badge" style={{ "--status-badge-color": statusColor }}>
                          <span
                            style={{ background: "rgba(255,255,255,0.85)" }}
                            className="dot"
                          ></span>
                          <span>{ticket.status || "-"}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
        {loading && (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
      </div>
      </div>

      <div className="Pagination">
        <div className="ss-page-size">
          <span className="ss-page-size-label">Items per page</span>
          <Select
            className="ss-mui-select ss-mui-select-compact"
            value={pageSize}
            onChange={handlePageSizeChange}
            variant="outlined"
            size="small"
            slotProps={{ paper: { className: "ss-select-menu-paper" } }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </div>
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      </div>

      {showTicketDetails && (
        <TicketDetails
          ticket={ticketDetails}
          onClose={() => dispatch(hideTicketDetailsPopup())}
        />
      )}
    </div>
  );
};

export default CompanyTickets;
